import * as Tabs from '@radix-ui/react-tabs';
import { Image, TrashAlt, Video } from '@styled-icons/boxicons-regular';
import { useState } from 'react';
import { useTPlateEditorRef } from '../../plate';
import { ColorInput, ColorItem, ColorPicker } from '../ColorPicker';
import { SelectMedia } from '../SelectMedia';
import styles from './BackgroundSettings.module.css';

interface BackgroundSettingsProps {
  currentValues: {
    background?: string
    backgroundImage?: string
    backgroundVideo?: string
    backgroundDarken?: boolean
  }
  colorOptions?: Record<string, string | undefined>
  onSelectColor?: (color: string) => void
  onSelectImage?: (url: string) => void
  onSelectVideo?: (url: string) => void
  onToggleDarken?: () => void
  onRemoveBackgroundMedia?: () => void
}

function BackgroundSettings(props: BackgroundSettingsProps) {
  const {
    currentValues,
    colorOptions = {},
    onSelectColor,
    onSelectImage,
    onSelectVideo,
    onToggleDarken,
    onRemoveBackgroundMedia,
  } = props;

  const {
    background: backgroundColor,
    backgroundImage,
    backgroundVideo,
    backgroundDarken,
  } = currentValues;

  const hasBackgroundMedia = !!(backgroundImage || backgroundVideo);

  const acceptedMediaTypes = [];
  if (onSelectImage) {
    acceptedMediaTypes.push({
      type: 'image',
      mime: 'image/*',
      label: 'Image',
      icon: Image,
    });
    if (onSelectVideo) {
      acceptedMediaTypes.push({
        type: 'video',
        mime: 'video/*',
        label: 'Video',
        icon: Video,
      });
    }
  }

  const editor = useTPlateEditorRef();
  const [loading, setLoading] = useState(false);

  const colorTab = (
    <div className={styles.tabContent}>
      <ColorPicker
        value={
          backgroundColor && colorOptions[backgroundColor]
            ? backgroundColor
            : ''
        }
        onChange={onSelectColor}
      >
        {Object.entries(colorOptions).map(([key, value]) => (
          <ColorItem key={key} colorKey={key} colorValue={value} />
        ))}
      </ColorPicker>

      <ColorInput
        predefinedColors={colorOptions}
        value={backgroundColor}
        onSubmit={onSelectColor}
      />
    </div>
  );

  const mediaTab = hasBackgroundMedia ? (
    <div className={styles.tabContent}>
      <div className={styles.backgroundPreview}>
        {backgroundVideo ? (
          <video autoPlay muted loop playsInline>
            <source src={backgroundVideo} />
          </video>
        ) : (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={backgroundImage} alt="current background" />
        )}
        <button
          type="button"
          className={styles.deleteBackgroundButton}
          onClick={onRemoveBackgroundMedia}
        >
          <TrashAlt size={20} />
        </button>
      </div>
      <div className={styles.darkenToggle}>
        <div>Darken Background</div>
        <div>
          <input
            id="backgroundDarkenToggle"
            type="checkbox"
            className="switch is-rounded is-primary"
            checked={!!backgroundDarken}
            onChange={onToggleDarken}
          />
          <label
            htmlFor="backgroundDarkenToggle"
            style={{ verticalAlign: 'middle' }}
          >
            {/*  */}
          </label>
        </div>
      </div>
    </div>
  ) : (
    <SelectMedia
      tabs={{
        upload: {
          accept: acceptedMediaTypes.map(({ mime }) => mime).join(','),
          button: 'Upload a media',
          onSelect: async (file) => {
            if (!editor.fileManager) return;

            setLoading(true);

            try {
              const url = await editor.fileManager.upload(file);
              if (file.type.startsWith('video/')) {
                onSelectVideo?.(url);
              } else {
                onSelectImage?.(url);
              }
            } catch (e) {
              setLoading(false);
            }
          },
          disabled: loading,
        },
        link: {
          acceptedTypes: acceptedMediaTypes,
          button: 'Use this media link',
          defaultValue: backgroundImage || backgroundVideo,
          placeholder: 'Enter media URL...',
          onSelect: (url, type) => {
            if (type === 'video') {
              onSelectVideo?.(url);
            } else {
              onSelectImage?.(url);
            }
          },
        },
        resources: {
          onSelect: (url) => {
            onSelectImage?.(url);
          },
        },
      }}
    />
  );

  const shouldRenderColorTab = !!onSelectColor;
  const shouldRenderMediaTab = !!(onSelectImage || onSelectVideo);

  // if only one tab then no need for tabs
  if (shouldRenderColorTab && shouldRenderMediaTab) {
    return (
      <Tabs.Root defaultValue="color" className={styles.tabRoot}>
        <Tabs.List className={styles.tabList}>
          <Tabs.Trigger value="color" className={styles.tabTrigger}>
            Color
          </Tabs.Trigger>
          <Tabs.Trigger value="media" className={styles.tabTrigger}>
            Media
          </Tabs.Trigger>
        </Tabs.List>
        <Tabs.Content value="color">{colorTab}</Tabs.Content>
        <Tabs.Content value="media">{mediaTab}</Tabs.Content>
      </Tabs.Root>
    );
  }

  return (
    <div className={styles.noTab}>
      {shouldRenderColorTab && colorTab}
      {shouldRenderMediaTab && mediaTab}
    </div>
  );
}

BackgroundSettings.defaultProps = {
  colorOptions: {},
  onSelectColor: undefined,
  onSelectImage: undefined,
  onSelectVideo: undefined,
  onToggleDarken: undefined,
  onRemoveBackgroundMedia: undefined,
};

export default BackgroundSettings;
